import { createApp } from 'vue';
import { createPinia } from 'pinia';
import * as Sentry from '@sentry/vue';

import router from './router/index';
import App from './App.vue';
import '@/assets/css/style.css';

const pinia = createPinia();
const app = createApp(App);

// Matomo Tag Manager bootstrap. Kept here rather than
// <script> in index.html so the Content-Security-Policy can drop
// script-src 'unsafe-inline'. The container script is loaded from
// VITE_MATOMO_URL, which the CSP script-src allowlists.
// Do NOT keep in a separate matomo.js file since vite requests it
// in dev mode and any ad blocker will break app loading
const matomoUrl = import.meta.env.VITE_MATOMO_URL;

const initMatomo = () => {
  if (!matomoUrl) return;
  const mtm = (window._mtm = window._mtm || []);
  mtm.push({ 'mtm.startTime': Date.now(), event: 'mtm.Start' });
  const script = document.createElement('script');
  script.async = true;
  script.src = matomoUrl;
  document.head.appendChild(script);
};

initMatomo();

// Performance tracing is off unless an environment opts in: traces on a payment
// flow carry request URLs (coupon codes, query params) we don't want to collect
// by default. Enable per environment with VITE_SENTRY_TRACES_SAMPLE_RATE (0-1).
// Errors are unaffected; they're governed by sampleRate.
const rawTraceRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
const envTraceRate = rawTraceRate?.trim() ? Number(rawTraceRate) : NaN;
const tracesSampleRate = envTraceRate >= 0 && envTraceRate <= 1 ? envTraceRate : 0;

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
    }),
  ],

  tracesSampleRate,
  environment: import.meta.env.VITE_SENTRY_ENV,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  ignoreErrors: [
    /^Could not find the requested \.$/,
    /^Could not find the requested UniqueCouponCode\.$/,
    /^VAT number is invalid$/,
    'Failed to load Turnstile.',
    /Nothing to reset found for provided container/,
  ],
  // Scanner bots and injected/eval'd scripts (e.g. Cloudflare challenges) throw
  // errors whose frames carry no script URL (filename "undefined"/"<anonymous>").
  // Our own bundles always report an https://... asset URL, so an exception where
  // no frame resolves to a real script can't be actionable — drop it.
  beforeSend(event) {
    const values = event.exception?.values ?? [];

    // Recurly.js surfaces transient/aborted request failures as unhandled
    // "api-error" exceptions caught by Sentry's global instrumentation
    // (e.g. its setTimeout wrappers). There's nothing to act on, and the
    // payment errors we capture ourselves are handled: true, so only drop
    // the unhandled ones.
    if (values.some(value =>
      value.type === 'api-error' && value.mechanism?.handled === false)) {
      return null;
    }

    const frames = values.flatMap(value => value.stacktrace?.frames ?? []);
    if (!frames.length) return event; // keep stackless captures (e.g. messages)
    const resolvable = frames.some(frame =>
      typeof frame.filename === 'string' && /^(https?:)?\/\//.test(frame.filename));
    return resolvable ? event : null;
  },
});

app.use(pinia);
app.use(router);
app.mount('#app');
